import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Search, Edit, Trash2, Eye} from 'lucide-react'
import { lumi } from "@/lib/lumi"

interface CadastroGeral {
  _id: string
  nomeCompleto: string
  cpfCnpj: string
  telefone: string
  endereco: string
  cep: string
  email: string
  createdAt: string
}

interface ListarCadastroGeralProps {
  onEdit?: (id: string) => void
}

export default function ListarCadastroGeral({ onEdit }: ListarCadastroGeralProps) {
  const [cadastros, setCadastros] = useState<CadastroGeral[]>([])
  const [filteredCadastros, setFilteredCadastros] = useState<CadastroGeral[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(true)
  const [viewingCadastro, setViewingCadastro] = useState<CadastroGeral | null>(null)

  useEffect(() => {
    loadCadastros()
  }, [])

  useEffect(() => {
    const filtered = cadastros.filter(cadastro =>
      cadastro.nomeCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cadastro.cpfCnpj.includes(searchTerm) ||
      cadastro.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    setFilteredCadastros(filtered)
  }, [searchTerm, cadastros])

  const loadCadastros = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.cadastro_geral.list({
        sort: { createdAt: -1 }
      })
      setCadastros(response.list || [])
      setFilteredCadastros(response.list || [])
    } catch (error) {
      console.error("Erro ao carregar cadastros:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string, nome: string) => {
    if (!window.confirm(`Deseja realmente excluir o cadastro de ${nome}?`)) {
      return
    }

    try {
      await lumi.entities.cadastro_geral.delete(id)
      alert("Cadastro excluído com sucesso!")
      loadCadastros()
    } catch (error) {
      console.error("Erro ao excluir cadastro:", error)
      alert("Erro ao excluir cadastro")
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("pt-BR")
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004161]"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-800">Cadastros Gerais</h2>
            <p className="text-sm text-gray-500">
              Total de {filteredCadastros.length} cadastro(s)
            </p>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, CPF/CNPJ ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nome/Empresa</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">CPF/CNPJ</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Telefone</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredCadastros.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nenhum cadastro encontrado
                  </td>
                </tr>
              ) : (
                filteredCadastros.map((cadastro) => (
                  <tr key={cadastro._id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{cadastro.nomeCompleto}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cadastro.cpfCnpj}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cadastro.telefone}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cadastro.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(cadastro.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setViewingCadastro(cadastro)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => onEdit?.(cadastro._id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(cadastro._id, cadastro.nomeCompleto)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Visualização */}
      {viewingCadastro && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Detalhes do Cadastro</h3>
                <button
                  onClick={() => setViewingCadastro(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Eye className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Nome Completo/Empresa
                  </label>
                  <p className="text-lg text-gray-900">{viewingCadastro.nomeCompleto}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      CPF/CNPJ
                    </label>
                    <p className="text-gray-900">{viewingCadastro.cpfCnpj}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Telefone
                    </label>
                    <p className="text-gray-900">{viewingCadastro.telefone}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Endereço
                  </label>
                  <p className="text-gray-900">{viewingCadastro.endereco}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      CEP
                    </label>
                    <p className="text-gray-900">{viewingCadastro.cep}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Email
                    </label>
                    <p className="text-gray-900">{viewingCadastro.email}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    Data de Cadastro
                  </label>
                  <p className="text-gray-900">{formatDate(viewingCadastro.createdAt)}</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setViewingCadastro(null)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
